/**
 * EmployeeTaskDetailPanel — one of a person's assigned tasks, read-only.
 *
 * Same two hosts and the same rule as `EmployeeCaseDetailPanel`: the person
 * surface's `detail` pane, and the `employeeTaskDetail` side panel. Both draw
 * the box and the header, so this draws neither — the `bg-white
 * dark:bg-neutral-900 … shadow-md` card it used to is gone, and its colours
 * are semantic tokens.
 *
 * TWO ROW SHAPES. The person page feeds it `POST /tasks/get_assigned_tasks`'s
 * platform shape (`title`, `status`, `due_date`, `priority`); the external
 * collaborator page still feeds it the legacy capitalised one (`Title`,
 * `Status`, `TaskDeadline`). It reads either, so neither host has to
 * translate — see `pick`.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../shared/TailwindComponents/Badge';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import { Field, Well } from '../../shared/brand/Field';
import { formatDateTime } from '../../../utils/functions';

interface Props {
  taskData: any;
}

const STATUS_COLORS: Record<string, BadgeColor> = {
  Active: 'green',
  active: 'green',
  todo: 'gray',
  in_progress: 'blue',
  Completed: 'indigo',
  completed: 'indigo',
  Deleted: 'red',
};

/** First value present, so one panel reads both row shapes. */
const pick = (...values: any[]) => values.find((v) => v !== undefined && v !== null && v !== '');

const EmployeeTaskDetailPanel: React.FC<Props> = ({ taskData }) => {
  const { t } = useTranslation();

  if (!taskData) {
    return (
      <p className="p-4 text-sm text-fg-muted text-center">{t('No task data found.')}</p>
    );
  }

  const title = pick(taskData.title, taskData.Title);
  const status = pick(taskData.status, taskData.Status);
  const description = pick(taskData.description, taskData.Description);
  const due = pick(taskData.due_date, taskData.TaskDeadline);
  const created = pick(taskData.created_at, taskData.CreationDate);
  const priority = pick(taskData.priority, taskData.Priority);
  const matterName = pick(taskData.matter_name, taskData.matter?.title);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto custom-scrollbar-main px-3 py-3 space-y-4">
      <div>
        <h3 className="text-base font-medium text-fg-strong">
          {title || t('Untitled Task')}
        </h3>
        {status && (
          <div className="mt-2">
            <Badge color={STATUS_COLORS[status] || 'gray'}>{t(status)}</Badge>
          </div>
        )}
      </div>

      {description && (
        <p className="text-sm text-fg-body leading-relaxed whitespace-pre-wrap">
          {description}
        </p>
      )}

      <Well className="space-y-3">
        <Field
          label={t('Deadline')}
          value={due ? formatDateTime(due) : undefined}
          emptyText="-"
        />
        <Field
          label={t('Created')}
          value={created ? formatDateTime(created) : undefined}
          emptyText="-"
        />
        <Field label={t('Priority')}>
          {priority ? <Badge color="red">{t(priority)}</Badge> : null}
        </Field>
        <Field label={t('Matter')} value={matterName} emptyText="-" />
        <Field label={t('Assigned By')} value={taskData.AssignedBy} emptyText="-" />
      </Well>
    </div>
  );
};

export default EmployeeTaskDetailPanel;
